/**
 * Upozornění do telefonu autora: nová připomínka, nový uživatel Úlníku.
 * Odběr se ukládá ve schránce v Cloudflare (chráněné heslem schránky).
 * iPhone: jen v aplikaci spuštěné z ikony na ploše, iOS 16.4 a novější.
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { FEEDBACK_URL } from './feedback';
import { isStandalone, platform } from './recommend';
import { saved } from './confirm';
import { Notice, Section, toast } from './ui';
import { Icon } from './icons';

const VAPID_PUBLIC = 'BAWImfXFNH5UqslbpR9vPxNe1X4_vZAwgo2sfDPnXidBq6DVJ_fP-pMb50zOEQiqJ9xgxuYzVY1WluD64lfuLA8';
const base = () => FEEDBACK_URL.replace(/\/feedback$/, '');
const PREFS = 'pushPrefs';
interface Prefs {
  feedback: boolean;
  installs: boolean;
}

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function keyBytes(b64: string) {
  const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64.length + 3) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

async function call(path: string, adminKey: string, body: unknown) {
  const r = await fetch(base() + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminKey}` },
    body: JSON.stringify(body),
  });
  if (r.status === 404) throw new Error('Schránka v Cloudflare ještě upozornění neumí – vložte do ní nový kód.');
  if (r.status === 401) throw new Error('Heslo schránky nesedí.');
  if (!r.ok) throw new Error(`Chyba ${r.status}.`);
}

export function PushSetting({ adminKey }: { adminKey: string }) {
  const [sub, setSub] = useState<PushSubscription | null | undefined>(undefined);
  const [prefs, setPrefs] = useState<Prefs>({ feedback: true, installs: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSetting<Prefs>(PREFS).then((p) => p && setPrefs(p));
    if (!supported()) return setSub(null);
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then(setSub, () => setSub(null));
  }, []);

  const ios = platform() === 'ios';
  if (!supported() || (ios && !isStandalone()))
    return (
      <Section title="Upozornění do telefonu">
        <Notice tone="info">
          {ios
            ? 'Na iPhonu fungují upozornění jen v aplikaci spuštěné z ikony na ploše (iOS 16.4 a novější). Otevřete Úlník z plochy a vraťte se sem.'
            : 'Tento prohlížeč upozornění nepodporuje.'}
        </Notice>
      </Section>
    );
  if (sub === undefined) return null;

  const save = async (p: Prefs, s: PushSubscription) => {
    await call('/push/subscribe', adminKey, { subscription: s.toJSON(), prefs: p });
    await setSetting(PREFS, p);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast(ios ? 'Upozornění nejsou povolená – zapněte je v Nastavení iPhonu → Oznámení → Úlník.' : 'Upozornění nejsou v telefonu povolená.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const s = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(VAPID_PUBLIC) }));
      await save(prefs, s);
      setSub(s);
      saved('Upozornění zapnuta');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upozornění se nepodařilo zapnout.');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!sub) return;
    setBusy(true);
    try {
      await call('/push/unsubscribe', adminKey, { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
      setSub(null);
      saved('Upozornění vypnuta');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (k: keyof Prefs, v: boolean) => {
    const p = { ...prefs, [k]: v };
    setPrefs(p);
    if (sub) {
      try {
        await save(p, sub);
        saved('Uloženo');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Nepodařilo se uložit.');
      }
    }
  };

  return (
    <Section title="Upozornění do telefonu">
      <p className="muted">Schránka vám pošle oznámení do telefonu, i když je aplikace zavřená.</p>
      <label className="check">
        <input type="checkbox" checked={prefs.feedback} onChange={(e) => void toggle('feedback', e.target.checked)} />
        Nová připomínka nebo hlášení chyby
      </label>
      <label className="check">
        <input type="checkbox" checked={prefs.installs} onChange={(e) => void toggle('installs', e.target.checked)} />
        Nový uživatel Úlníku (nová instalace)
      </label>
      {sub ? (
        <div className="quick-row">
          <button
            className="btn"
            disabled={busy}
            onClick={() =>
              call('/push/test', adminKey, {}).then(
                () => toast('Zkušební upozornění odesláno – mělo by přijít během chvilky.'),
                (e) => toast(e instanceof Error ? e.message : 'Nepodařilo se odeslat.'),
              )
            }
          >
            <Icon name="mail" size={20} /> Zkusit
          </button>
          <button className="btn" disabled={busy} onClick={disable}>
            Vypnout upozornění
          </button>
        </div>
      ) : (
        <button className="btn primary wide" disabled={busy} onClick={enable}>
          {busy ? 'Zapínám…' : 'Zapnout upozornění'}
        </button>
      )}
      {sub && <p className="small ok-text">Upozornění jsou v tomto telefonu zapnutá.</p>}
    </Section>
  );
}
